import React from 'react';
import {
  buildImageTransformPatch,
  clampImageAxisScale,
  clampImagePositionPercent,
  clampImageZoomPercent,
  normalizeImageRotation,
  normalizeImageTransformStyle,
  type ImageAdjustInteractionKind,
  type ImageTransformHandleKind,
} from './imageBlockEditing';

type ImageTransformInteraction = {
  pointerId: number;
  blockId: string;
  kind: ImageAdjustInteractionKind;
  startClientX: number;
  startClientY: number;
  startPositionX: number;
  startPositionY: number;
  startScaleX: number;
  startScaleY: number;
  startRotation: number;
  startAngleDeg: number;
  bounds: DOMRect;
  nextStyle: Record<string, unknown>;
  axis?: 'x' | 'y';
  directionX?: -1 | 1;
  directionY?: -1 | 1;
};

type UseImageTransformInteractionsArgs = {
  isImageAdjustMode: boolean;
  activeAdjustBlockId: string | null;
  selectedBlockId?: string | null;
  onPreviewBlockStyle?: (blockId: string, styleOverride: Record<string, unknown> | null) => void;
  onCommitBlockStyle?: (blockId: string, styleOverride: Record<string, unknown> | null) => void;
};

type WheelLikeEvent = {
  deltaY: number;
  preventDefault: () => void;
  stopPropagation: () => void;
};

function toAngleDegrees(clientX: number, clientY: number, bounds: DOMRect) {
  const centerX = bounds.left + bounds.width / 2;
  const centerY = bounds.top + bounds.height / 2;
  return (Math.atan2(clientY - centerY, clientX - centerX) * 180) / Math.PI;
}

function resolveHandleInteraction(
  handleKind: ImageTransformHandleKind
): Pick<ImageTransformInteraction, 'kind' | 'axis' | 'directionX' | 'directionY'> {
  switch (handleKind) {
    case 'corner-nw':
      return { kind: 'scale-uniform', directionX: -1, directionY: -1 };
    case 'corner-ne':
      return { kind: 'scale-uniform', directionX: 1, directionY: -1 };
    case 'corner-se':
      return { kind: 'scale-uniform', directionX: 1, directionY: 1 };
    case 'corner-sw':
      return { kind: 'scale-uniform', directionX: -1, directionY: 1 };
    case 'side-n':
      return { kind: 'scale-y', axis: 'y', directionY: -1 };
    case 'side-e':
      return { kind: 'scale-x', axis: 'x', directionX: 1 };
    case 'side-s':
      return { kind: 'scale-y', axis: 'y', directionY: 1 };
    case 'side-w':
      return { kind: 'scale-x', axis: 'x', directionX: -1 };
    case 'rotate-nw':
    case 'rotate-ne':
    case 'rotate-se':
    case 'rotate-sw':
      return { kind: 'rotate' };
  }
}

export function useImageTransformInteractions({
  isImageAdjustMode,
  activeAdjustBlockId,
  selectedBlockId,
  onPreviewBlockStyle,
  onCommitBlockStyle,
}: UseImageTransformInteractionsArgs) {
  const draftStylesRef = React.useRef<Record<string, Record<string, unknown>>>({});
  const commitTimersRef = React.useRef<Record<string, number>>({});
  const interactionRef = React.useRef<ImageTransformInteraction | null>(null);
  const previewBlockStyleRef = React.useRef(onPreviewBlockStyle);
  const commitBlockStyleRef = React.useRef(onCommitBlockStyle);
  const [activeInteraction, setActiveInteraction] = React.useState<{
    blockId: string;
    kind: ImageAdjustInteractionKind;
  } | null>(null);

  React.useEffect(() => {
    previewBlockStyleRef.current = onPreviewBlockStyle;
    commitBlockStyleRef.current = onCommitBlockStyle;
  }, [onCommitBlockStyle, onPreviewBlockStyle]);

  const clearCommitTimer = React.useCallback((blockId: string) => {
    const timerId = commitTimersRef.current[blockId];
    if (!timerId) return;
    window.clearTimeout(timerId);
    delete commitTimersRef.current[blockId];
  }, []);

  const pushPreview = React.useCallback(
    (blockId: string, nextStyle: Record<string, unknown>) => {
      draftStylesRef.current[blockId] = nextStyle;
      previewBlockStyleRef.current?.(blockId, nextStyle);
    },
    []
  );

  const commitStyle = React.useCallback(
    (blockId: string) => {
      clearCommitTimer(blockId);
      const draftStyle = draftStylesRef.current[blockId];
      if (!draftStyle) return;
      commitBlockStyleRef.current?.(blockId, draftStyle);
      delete draftStylesRef.current[blockId];
    },
    [clearCommitTimer]
  );

  const scheduleCommit = React.useCallback(
    (blockId: string) => {
      clearCommitTimer(blockId);
      commitTimersRef.current[blockId] = window.setTimeout(() => {
        commitStyle(blockId);
      }, 180);
    },
    [clearCommitTimer, commitStyle]
  );

  const beginSurfaceMove = React.useCallback(
    (
      event: React.PointerEvent<HTMLDivElement>,
      blockId: string,
      styleValue: Record<string, unknown> | undefined
    ) => {
      const isActive = isImageAdjustMode && activeAdjustBlockId === blockId;
      if (!isActive || selectedBlockId !== blockId || event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      clearCommitTimer(blockId);
      const normalized = normalizeImageTransformStyle(styleValue);
      const nextStyle = buildImageTransformPatch(styleValue, normalized);
      interactionRef.current = {
        pointerId: event.pointerId,
        blockId,
        kind: 'move',
        startClientX: event.clientX,
        startClientY: event.clientY,
        startPositionX: normalized.imagePositionX,
        startPositionY: normalized.imagePositionY,
        startScaleX: normalized.imageScaleX,
        startScaleY: normalized.imageScaleY,
        startRotation: normalized.imageRotation,
        startAngleDeg: 0,
        bounds: event.currentTarget.getBoundingClientRect(),
        nextStyle,
      };
      draftStylesRef.current[blockId] = nextStyle;
      setActiveInteraction({ blockId, kind: 'move' });
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {}
    },
    [activeAdjustBlockId, clearCommitTimer, isImageAdjustMode, selectedBlockId]
  );

  const moveSurfaceMove = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const interaction = interactionRef.current;
      if (!interaction || interaction.pointerId !== event.pointerId || interaction.kind !== 'move') return;
      const nextStyle = {
        ...interaction.nextStyle,
        imagePositionX: clampImagePositionPercent(
          interaction.startPositionX +
            ((event.clientX - interaction.startClientX) / Math.max(1, interaction.bounds.width)) * 100
        ),
        imagePositionY: clampImagePositionPercent(
          interaction.startPositionY +
            ((event.clientY - interaction.startClientY) / Math.max(1, interaction.bounds.height)) * 100
        ),
      };
      interaction.nextStyle = nextStyle;
      pushPreview(interaction.blockId, nextStyle);
      event.preventDefault();
      event.stopPropagation();
    },
    [pushPreview]
  );

  const endSurfaceMove = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const interaction = interactionRef.current;
      if (!interaction || interaction.pointerId !== event.pointerId || interaction.kind !== 'move') return;
      interactionRef.current = null;
      setActiveInteraction(null);
      commitStyle(interaction.blockId);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      event.preventDefault();
      event.stopPropagation();
    },
    [commitStyle]
  );

  const handleWheelZoom = React.useCallback(
    (
      event: WheelLikeEvent,
      blockId: string,
      styleValue: Record<string, unknown> | undefined
    ) => {
      const isActive = isImageAdjustMode && activeAdjustBlockId === blockId;
      if (!isActive || selectedBlockId !== blockId) return;
      const draftStyle =
        draftStylesRef.current[blockId] ??
        buildImageTransformPatch(styleValue, normalizeImageTransformStyle(styleValue));
      const currentZoom = Number(draftStyle.imageScale ?? 100);
      const nextStyle = {
        ...draftStyle,
        imageScale: clampImageZoomPercent(currentZoom * Math.exp(-event.deltaY * 0.0015)),
      };
      pushPreview(blockId, nextStyle);
      scheduleCommit(blockId);
      event.preventDefault();
      event.stopPropagation();
    },
    [activeAdjustBlockId, isImageAdjustMode, pushPreview, scheduleCommit, selectedBlockId]
  );

  const beginHandleInteraction = React.useCallback(
    (
      event: React.PointerEvent<HTMLButtonElement>,
      blockId: string,
      styleValue: Record<string, unknown> | undefined,
      handleKind: ImageTransformHandleKind,
      bounds: DOMRect | null
    ) => {
      const isActive = isImageAdjustMode && activeAdjustBlockId === blockId;
      if (!isActive || selectedBlockId !== blockId || event.button !== 0 || !bounds) return;
      event.preventDefault();
      event.stopPropagation();

      clearCommitTimer(blockId);
      const normalized = normalizeImageTransformStyle(styleValue);
      const nextStyle = buildImageTransformPatch(styleValue, normalized);
      const handle = resolveHandleInteraction(handleKind);
      interactionRef.current = {
        pointerId: event.pointerId,
        blockId,
        ...handle,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startPositionX: normalized.imagePositionX,
        startPositionY: normalized.imagePositionY,
        startScaleX: normalized.imageScaleX,
        startScaleY: normalized.imageScaleY,
        startRotation: normalized.imageRotation,
        startAngleDeg: toAngleDegrees(event.clientX, event.clientY, bounds),
        bounds,
        nextStyle,
      };
      draftStylesRef.current[blockId] = nextStyle;
      setActiveInteraction({ blockId, kind: handle.kind });
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {}
    },
    [activeAdjustBlockId, clearCommitTimer, isImageAdjustMode, selectedBlockId]
  );

  const moveHandleInteraction = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const interaction = interactionRef.current;
      if (!interaction || interaction.pointerId !== event.pointerId || interaction.kind === 'move') return;
      const deltaX = (event.clientX - interaction.startClientX) / Math.max(1, interaction.bounds.width);
      const deltaY = (event.clientY - interaction.startClientY) / Math.max(1, interaction.bounds.height);
      let nextStyle = interaction.nextStyle;

      if (interaction.kind === 'scale-uniform') {
        const signedDelta =
          ((interaction.directionX ?? 1) * deltaX + (interaction.directionY ?? 1) * deltaY) / 2;
        const factor = Math.max(0.1, 1 + signedDelta);
        nextStyle = {
          ...nextStyle,
          imageScaleX: clampImageAxisScale(interaction.startScaleX * factor),
          imageScaleY: clampImageAxisScale(interaction.startScaleY * factor),
        };
      } else if (interaction.kind === 'scale-x') {
        const signedDelta = (interaction.directionX ?? 1) * deltaX;
        nextStyle = {
          ...nextStyle,
          imageScaleX: clampImageAxisScale(interaction.startScaleX * Math.max(0.1, 1 + signedDelta)),
        };
      } else if (interaction.kind === 'scale-y') {
        const signedDelta = (interaction.directionY ?? 1) * deltaY;
        nextStyle = {
          ...nextStyle,
          imageScaleY: clampImageAxisScale(interaction.startScaleY * Math.max(0.1, 1 + signedDelta)),
        };
      } else if (interaction.kind === 'rotate') {
        const currentAngleDeg = toAngleDegrees(event.clientX, event.clientY, interaction.bounds);
        nextStyle = {
          ...nextStyle,
          imageRotation: normalizeImageRotation(
            interaction.startRotation + (currentAngleDeg - interaction.startAngleDeg)
          ),
        };
      }

      interaction.nextStyle = nextStyle;
      pushPreview(interaction.blockId, nextStyle);
      event.preventDefault();
      event.stopPropagation();
    },
    [pushPreview]
  );

  const endHandleInteraction = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const interaction = interactionRef.current;
      if (!interaction || interaction.pointerId !== event.pointerId || interaction.kind === 'move') return;
      interactionRef.current = null;
      setActiveInteraction(null);
      commitStyle(interaction.blockId);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      event.preventDefault();
      event.stopPropagation();
    },
    [commitStyle]
  );

  React.useEffect(() => {
    return () => {
      Object.values(commitTimersRef.current).forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      commitTimersRef.current = {};
      draftStylesRef.current = {};
      interactionRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    if (isImageAdjustMode) return;
    const pendingDraftBlockIds = Object.keys(draftStylesRef.current);
    const hasActiveInteraction = interactionRef.current !== null;
    if (pendingDraftBlockIds.length === 0 && !hasActiveInteraction && activeInteraction === null) {
      return;
    }
    pendingDraftBlockIds.forEach((blockId) => {
      commitStyle(blockId);
    });
    interactionRef.current = null;
    if (activeInteraction !== null) {
      setActiveInteraction(null);
    }
  }, [activeInteraction, commitStyle, isImageAdjustMode]);

  return {
    activeInteraction,
    beginSurfaceMove,
    moveSurfaceMove,
    endSurfaceMove,
    handleWheelZoom,
    beginHandleInteraction,
    moveHandleInteraction,
    endHandleInteraction,
  };
}
